import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../../context/AuthContext";
import { getHocSinhByLop } from "../../services/hocSinhService";
import { getDiemDanhByNgay } from "../../services/diemDanhService";
import { getCache, setCache } from "../../services/cacheService";

const GVPhuDashboard = () => {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const lopId = user?.maLopCN || "";
  const [hocSinhList, setHocSinhList] = useState([]);
  const [diemDanhData, setDiemDanhData] = useState([]);
  const [loading, setLoading] = useState(true);
  const today = new Date();
  const isSunday = today.getDay() === 0;

  const getSundayDate = () => { const d = new Date(); d.setDate(d.getDate() - d.getDay()); return d; };
  const sundayDate = getSundayDate();
  const ngayCN = sundayDate.toISOString().split('T')[0];
  const ngayCNFormatted = sundayDate.toLocaleDateString('vi-VN', { day: 'numeric', month: 'long' });

  useEffect(() => {
    const fetchData = async () => {
      const cached = getCache(`gvphu_${lopId}_${ngayCN}`);
      if (cached) { setHocSinhList(cached.hs); setDiemDanhData(cached.dd); setLoading(false); return; }
      setLoading(true);
      try {
        if (lopId) { const [hs, dd] = await Promise.all([getHocSinhByLop(lopId), getDiemDanhByNgay(lopId, ngayCN)]); setHocSinhList(hs); setDiemDanhData(dd); setCache(`gvphu_${lopId}_${ngayCN}`, { hs, dd }); }
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetchData();
  }, [lopId, ngayCN]);

  const getRoleName = () => 'GV Phụ';
  const siSo = hocSinhList.length;
  const daDiemDanh = diemDanhData.filter(d => d.trangThai === 'coMat' || d.trangThai === 'diTre').length;
  const vangHomNay = diemDanhData.filter(d => d.trangThai === 'vangPhep' || d.trangThai === 'vangKhongPhep').length;
  const chuaDiemDanh = siSo - diemDanhData.length;
  const daDiemDanhXong = diemDanhData.length > 0 && chuaDiemDanh === 0;
  const formatDate = () => today.toLocaleDateString('vi-VN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const tenLopHienThi = user?.tenLop || user?.maLopCN || 'Chưa có lớp';

  if (loading) return <div className="flex items-center justify-center h-64"><span className="material-symbols-outlined text-primary animate-spin text-4xl">sync</span></div>;

  return (
    <div className="space-y-5 md:space-y-6">
      <section className="flex flex-col md:flex-row justify-between items-start md:items-end gap-3">
        <div>
          <h1 className="text-[24px] md:text-[28px] font-bold text-primary">Xin chào, {getRoleName()} {user?.tenThanh ? `${user?.tenThanh} ` : ''}{user?.hoTen || '...'}</h1>
          <p className="text-body-md text-on-surface-variant mt-1 flex items-center gap-2"><span className="px-2 py-0.5 bg-primary-container text-on-primary-container rounded-full text-label-caps">{tenLopHienThi}</span><span className="text-caption">Năm học 2025-2026</span></p>
        </div>
        <div className="text-caption text-on-surface-variant flex items-center gap-1 bg-surface-container-low px-3 py-1.5 rounded-full"><span className="material-symbols-outlined text-sm">calendar_today</span>{formatDate()}</div>
      </section>

      {isSunday && !daDiemDanhXong && (
        <div className="bg-[#FFF8E1] border border-[#FFB300]/30 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 animate-pulse">
          <span className="material-symbols-outlined text-[#FF8F00] text-3xl">campaign</span>
          <div className="flex-1"><p className="text-body-lg font-bold">Cần điểm danh hôm nay!</p><p className="text-body-md text-on-surface-variant">Còn <strong className="text-error">{siSo} HS</strong> chưa điểm danh</p></div>
          <button onClick={() => navigate('/diem-danh')} className="bg-primary text-on-primary px-5 py-3 rounded-xl font-bold">Điểm danh ngay →</button>
        </div>
      )}
      {((isSunday && daDiemDanhXong) || !isSunday) && diemDanhData.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
          <span className="material-symbols-outlined text-green-600 text-3xl">check_circle</span>
          <div className="flex-1"><p className="text-body-lg text-green-800 font-bold">{isSunday ? 'Đã điểm danh xong!' : `Điểm danh CN ${ngayCNFormatted}`}</p><p className="text-body-md text-green-700">{daDiemDanh} có mặt · {vangHomNay} vắng</p></div>
          <button onClick={() => navigate('/diem-danh')} className="text-body-md text-green-700 font-semibold hover:underline">Xem chi tiết →</button>
        </div>
      )}

      <section className="grid grid-cols-3 gap-2 md:gap-3">
        {[
          { icon: 'groups', label: 'Sĩ số', value: siSo, path: '/quan-ly-hoc-sinh', color: 'text-primary', bg: 'bg-primary-container' },
          { icon: 'check_circle', label: 'Có mặt', value: diemDanhData.length > 0 ? daDiemDanh : '—', path: '/diem-danh', color: 'text-secondary', bg: 'bg-secondary-container' },
          { icon: 'person_off', label: 'Vắng', value: diemDanhData.length > 0 ? vangHomNay : '—', path: '/diem-danh', color: 'text-error', bg: 'bg-error-container' },
        ].map((c, i) => (
          <button key={i} onClick={() => navigate(c.path)} className="bg-surface rounded-xl p-4 shadow-sm border border-outline-variant flex flex-col justify-between hover:shadow-md transition-all active:scale-[0.98] text-left">
            <div className="flex justify-between items-start mb-2"><span className="text-body-md text-on-surface-variant">{c.label}</span><div className={`p-1.5 rounded-full ${c.bg}`}><span className={`material-symbols-outlined ${c.color} text-xl`}>{c.icon}</span></div></div>
            <div className={`text-[32px] font-bold ${c.color}`}>{c.value}</div>
          </button>
        ))}
      </section>

      <section>
        <h2 className="text-section-title text-primary mb-3 font-bold">Thao tác nhanh</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {[
            { icon: 'fact_check', label: 'Điểm danh', path: '/diem-danh', color: 'bg-primary' },
            { icon: 'edit_document', label: 'Nhập điểm', path: '/nhap-diem', color: 'bg-surface border border-primary/20 text-primary' },
            { icon: 'folder_shared', label: 'Hồ sơ HS', path: '/quan-ly-hoc-sinh', color: 'bg-surface border border-primary/20 text-primary' },
          ].map((a, i) => (
            <button key={i} onClick={() => navigate(a.path)} className={`${a.color} rounded-xl p-4 flex flex-col items-center justify-center gap-2 hover:opacity-90 transition-opacity active:scale-95`}>
              <span className="material-symbols-outlined text-3xl">{a.icon}</span><span className="text-body-lg font-semibold">{a.label}</span>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
};

export default GVPhuDashboard;